import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { formatCurrency } from '../../utils/formatters';
import paymentService from '../../services/payment.service';
import CloseIcon from '@mui/icons-material/Close';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

interface CheckoutModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  referralId: string;
  vacancyId: string;
  candidateName: string;
  bountyAmount: number;
  bountyCurrency: string;
  darkMode?: boolean;
}

const CheckoutModal: React.FC<CheckoutModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  referralId,
  vacancyId,
  candidateName,
  bountyAmount,
  bountyCurrency,
  darkMode,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [cardNumber, setCardNumber] = useState('');
  const [expiry, setExpiry] = useState('');
  const [cvc, setCvc] = useState('');

  if (!isOpen) return null;

  const platformFee = paymentService.getPlatformFee(bountyAmount);
  const total = paymentService.getTotal(bountyAmount);

  const handlePay = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const intent = await paymentService.createPaymentIntent(vacancyId, bountyAmount, bountyCurrency);
      await paymentService.confirmHirePayment(referralId, intent.id);
      setSuccess(true);
      setTimeout(() => {
        onSuccess();
        onClose();
      }, 2000);
    } catch (err: any) {
      setError(err.message || 'Payment failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !loading) onClose();
  };

  return ReactDOM.createPortal(
    <div className={`modal-backdrop ${darkMode ? 'dark' : ''}`} onClick={handleBackdropClick}>
      <div className="modal-content checkout-modal">
        <button className="modal-close-btn" onClick={onClose} aria-label="Close" disabled={loading}>
          <CloseIcon />
        </button>

        {success ? (
          <div className="checkout-success">
            <CheckCircleOutlineIcon sx={{ fontSize: 64, color: '#4caf50' }} />
            <h2>Payment Successful!</h2>
            <p>The referral bounty for {candidateName} has been confirmed.</p>
            <p className="checkout-success-note">The referrer will receive their payout after the 14-day hold period.</p>
          </div>
        ) : (
          <>
            <h2>Confirm Hire & Pay Bounty</h2>
            <p className="modal-subtitle">You are hiring {candidateName}</p>

            <div className="checkout-breakdown">
              <div className="checkout-line">
                <span>Referral Bounty</span>
                <span>{formatCurrency(bountyAmount, bountyCurrency)}</span>
              </div>
              <div className="checkout-line">
                <span>Platform Fee (10%)</span>
                <span>{formatCurrency(platformFee, bountyCurrency)}</span>
              </div>
              <div className="checkout-line checkout-total">
                <span>Total</span>
                <span>{formatCurrency(total, bountyCurrency)}</span>
              </div>
            </div>

            {error && <div className="modal-error">{error}</div>}

            <form onSubmit={handlePay}>
              <div className="form-group">
                <label>Card Number</label>
                <input
                  type="text"
                  value={cardNumber}
                  onChange={e => setCardNumber(e.target.value)}
                  placeholder="4242 4242 4242 4242"
                  maxLength={19}
                  required
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Expiry</label>
                  <input
                    type="text"
                    value={expiry}
                    onChange={e => setExpiry(e.target.value)}
                    placeholder="MM/YY"
                    maxLength={5}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>CVC</label>
                  <input
                    type="text"
                    value={cvc}
                    onChange={e => setCvc(e.target.value)}
                    placeholder="123"
                    maxLength={4}
                    required
                  />
                </div>
              </div>

              <button type="submit" className="btn-primary btn-checkout" disabled={loading}>
                <LockOutlinedIcon sx={{ fontSize: 18, marginRight: '6px' }} />
                {loading ? 'Processing...' : `Pay ${formatCurrency(total, bountyCurrency)} & Confirm Hire`}
              </button>
            </form>

            <p className="checkout-secure-note">
              <LockOutlinedIcon sx={{ fontSize: 14, verticalAlign: 'middle', marginRight: '4px' }} />
              Secure payment powered by Stripe
            </p>
          </>
        )}
      </div>
    </div>,
    document.body
  );
};

export default CheckoutModal;
